import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtPrice(p, currency = "USD") {
  if (!p) return "";
  const n = Number(p);
  if (Number.isNaN(n)) return `$${p}`;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
}

function AffirmKlarna() {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/50">
      <span className="px-2 py-1 border border-white/15 bg-white/[0.03] font-semibold">Affirm</span>
      <span className="px-2 py-1 border border-white/15 bg-white/[0.03] font-semibold">Klarna</span>
      <span className="hidden sm:inline">· pay over time</span>
    </div>
  );
}

function ProductCard({ p, onOpen, i }) {
  return (
    <motion.div
      data-testid={`shop-product-${p.id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
      className="group relative bg-[#131315] border border-[#262629] hover:border-[#3FE1D1]/50 transition-all duration-300 hover:-translate-y-2 flex flex-col"
    >
      <button
        onClick={() => onOpen(p)}
        data-testid={`shop-details-${p.id}`}
        className="relative aspect-[4/5] overflow-hidden bg-black text-left"
      >
        {p.image ? (
          <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 text-sm">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#131315] via-transparent to-transparent opacity-70" />
        {p.tag && (
          <span className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 ${p.available ? "bg-[#3FE1D1] text-[#050505]" : "bg-white/10 text-white/80 backdrop-blur-sm"}`}>
            {p.tag}
          </span>
        )}
      </button>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display font-bold text-white text-lg leading-tight">{p.title}</h3>
        <div className="mt-auto pt-5 flex items-center justify-between">
          <span className="font-impact text-[#3FE1D1] text-3xl leading-none">{fmtPrice(p.price, p.currency)}</span>
          <a
            data-testid={`shop-buy-${p.id}`}
            href={p.url}
            target="_blank"
            rel="noreferrer"
            className="group/btn inline-flex items-center gap-1.5 bg-white text-[#050505] font-bold uppercase text-xs tracking-[0.12em] px-5 py-3 hover:bg-[#3FE1D1] transition-colors duration-200"
          >
            Buy
            <ArrowUpRight size={15} className="transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function ShopPage() {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/shop/collections`)
      .then((r) => {
        const cols = r.data.collections || [];
        setCollections(cols);
        if (cols.length) setActive(cols[0].handle);
        else setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const load = useCallback((handle) => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/shop/products`, { params: { collection: handle } })
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (active) load(active);
  }, [active, load]);

  return (
    <main data-testid="shop-page" className="bg-[#050505] text-white min-h-screen">
      {/* Page hero */}
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 border-b border-white/10 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-0 h-[420px] w-[420px] rounded-full bg-[#3FE1D1]/10 blur-[140px]" />
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#3FE1D1] font-medium">The Shop</span>
          <h1 className="mt-4 font-display font-extrabold uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.88]">
            Signed. Sealed.<br />
            <span className="text-[#3FE1D1]">Legendary.</span>
          </h1>
          <div className="mt-6 flex items-center gap-2 text-white/55 text-sm md:text-base max-w-xl">
            <ShieldCheck size={18} className="text-[#3FE1D1] flex-shrink-0" />
            Every piece Witnessed OR Authenticated. Secure Shopify checkout with Affirm &amp; Klarna.
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-[68px] z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/10">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 flex gap-3 overflow-x-auto no-scrollbar py-4">
          {collections.map((c) => (
            <button
              key={c.handle}
              data-testid={`shop-collection-tab-${c.handle}`}
              onClick={() => setActive(c.handle)}
              className={`whitespace-nowrap px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold border transition-colors ${
                active === c.handle
                  ? "bg-[#3FE1D1] border-[#3FE1D1] text-[#050505]"
                  : "border-[#262629] text-white/70 hover:border-[#3FE1D1]/50"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <section className="w-full max-w-[1440px] mx-auto px-6 md:px-12 py-14 md:py-20 min-h-[40vh]">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-[#131315] border border-[#262629] animate-pulse">
                <div className="aspect-[4/5] bg-white/[0.03]" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-white/5 w-3/4" />
                  <div className="h-6 bg-white/5 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div data-testid="shop-error" className="py-24 text-center text-white/50">
            <p className="font-display text-2xl text-white">Couldn't load the shop.</p>
            <p className="mt-2">Please refresh, or shop directly on our store.</p>
            <a
              href="https://monthlypops.com"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 border border-[#3FE1D1] text-[#3FE1D1] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-[#3FE1D1]/10 transition-colors"
            >
              Visit Store
              <ArrowUpRight size={18} />
            </a>
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center text-white/50">
            <p className="font-display text-2xl text-white">Nothing here yet.</p>
            <p className="mt-2">New drops land constantly — check back soon or catch us live.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} p={p} i={i} onOpen={setModal} />
            ))}
          </div>
        )}
      </section>

      {/* Product modal */}
      <AnimatePresence>
        {modal && (
          <motion.div
            data-testid="shop-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl bg-[#131315] border border-[#262629] grid grid-cols-1 md:grid-cols-2 max-h-[88vh] overflow-hidden"
            >
              <button
                data-testid="shop-modal-close"
                onClick={() => setModal(null)}
                className="absolute top-4 right-4 z-10 h-10 w-10 bg-[#050505]/70 border border-white/15 flex items-center justify-center text-white hover:bg-[#3FE1D1] hover:text-black transition-colors"
              >
                <X size={18} />
              </button>
              <div className="relative aspect-square md:aspect-auto bg-black">
                {modal.image && <img src={modal.image} alt={modal.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-8 md:p-10 overflow-y-auto">
                {modal.tag && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 ${modal.available ? "bg-[#3FE1D1] text-[#050505]" : "bg-white/10 text-white/80"}`}>
                    {modal.tag}
                  </span>
                )}
                <h2 className="mt-5 font-display font-extrabold text-white text-2xl md:text-3xl leading-tight">{modal.title}</h2>
                <p className="mt-4 text-white/60 leading-relaxed text-sm md:text-base">{modal.description || "Authenticated collectible from Monthly Pops."}</p>
                <div className="mt-8 font-impact text-[#3FE1D1] text-5xl leading-none">{fmtPrice(modal.price, modal.currency)}</div>
                <a
                  data-testid="shop-modal-buy"
                  href={modal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-white transition-colors"
                >
                  Buy on Shopify
                  <ArrowUpRight size={18} />
                </a>
                <div className="mt-5 pt-5 border-t border-white/10">
                  <AffirmKlarna />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
